import { ColDef } from 'ag-grid-enterprise'
import { TFunction } from 'react-i18next'
import { Transaction, Voucher } from '../../../../pages/reporting/vouchers/types'
import { parseDate } from '../../../../config/dayjsConfig'
import { getTransactionColumnDefs } from './transactionColumnDefs'

export interface TransactionWithVoucher extends Transaction {
  voucherGuid: Voucher['guid']
  voucherType: Voucher['type']
  voucherStatus: Voucher['status']
  invoicePartnerName: string | undefined
  invoiceNumber: number | string | undefined
  invoicePaymentDate: string | undefined
}

const textFilter = {
  filter: 'agTextColumnFilter',
  filterParams: { filterOptions: ['contains'], maxNumConditions: 1, buttons: ['reset'] }
}

// Fields that have backend filter support
const BACKEND_FILTERABLE_FIELDS = new Set([
  'voucherGuid',
  'voucherType',
  'invoicePartnerName',
  'date',
  'description',
  'invoiceNumber'
])

const voucherPrefixColumns = (t: TFunction): ColDef<TransactionWithVoucher>[] => [
  {
    headerName: t('vouchersPage:voucherGuid'),
    field: 'voucherGuid',
    width: 120,
    ...textFilter
  },
  {
    headerName: t('vouchersPage:voucherType'),
    field: 'voucherType',
    width: 130,
    ...textFilter
  },
  {
    headerName: t('vouchersPage:voucherStatus'),
    field: 'voucherStatus',
    width: 130,
    valueFormatter: params => {
      const status = params.value
      if (!status) return ''
      if (typeof status === 'string') return status
      return Object.values(status).flat().join(', ')
    }
  },
  {
    headerName: t('vouchersPage:invoicePartnerName'),
    field: 'invoicePartnerName',
    width: 150
  }
]

const voucherSuffixColumns = (t: TFunction): ColDef<TransactionWithVoucher>[] => [
  {
    headerName: t('vouchersPage:invoiceNumber'),
    field: 'invoiceNumber',
    width: 130,
    ...textFilter,
    filterValueGetter: params => (params.data?.invoiceNumber != null ? String(params.data.invoiceNumber) : '')
  },
  {
    headerName: t('vouchersPage:invoicePaymentDate'),
    field: 'invoicePaymentDate',
    width: 140,
    valueFormatter: params => {
      const date = params.value
      return date ? parseDate(date)?.format('DD.MM.YYYY') || '' : ''
    }
  }
]

export const getTransactionWithVoucherColumnDefs = (
  t: TFunction,
  transactions: TransactionWithVoucher[],
  showFilters = true
): ColDef<TransactionWithVoucher>[] => {
  const transactionCols = getTransactionColumnDefs(t, transactions, true).map(col => {
    if (!showFilters || !BACKEND_FILTERABLE_FIELDS.has(col.field as string)) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { filter, filterParams, ...rest } = col as any
      return rest as ColDef<TransactionWithVoucher>
    }
    return col as unknown as ColDef<TransactionWithVoucher>
  })

  const prefixCols = showFilters
    ? voucherPrefixColumns(t)
    : // eslint-disable-next-line @typescript-eslint/no-unused-vars
      voucherPrefixColumns(t).map(({ filter, filterParams, ...rest }) => rest)
  const suffixCols = showFilters
    ? voucherSuffixColumns(t)
    : // eslint-disable-next-line @typescript-eslint/no-unused-vars
      voucherSuffixColumns(t).map(({ filter, filterParams, ...rest }) => rest)

  return [...prefixCols, ...transactionCols, ...suffixCols]
}
